import {
  EdgeRemoveChange,
  IsValidConnection,
  OnEdgesChange,
  OnNodesChange,
  useReactFlow,
} from "@xyflow/react";
import { AppEdge } from "features/entities/EntityCreatePage/EntityGraph/edges/types";
import { DRAFT_NODE_ID } from "features/entities/EntityCreatePage/EntityGraph/entityGraphConstants";
import { AppNode } from "features/entities/EntityCreatePage/EntityGraph/nodes/types";
import debounce from "lodash-es/debounce";
import find from "lodash-es/find";
import partition from "lodash-es/partition";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "shared/components/toast/useToast";

export const useGraphValidation = (
  _onNodesChange: OnNodesChange<AppNode>,
  _onEdgesChange: OnEdgesChange<AppEdge>,
) => {
  const toast = useToast();
  const { t } = useTranslation(["common", "entity"]);
  const { getEdges } = useReactFlow();

  const onNodesChange = useCallback<OnNodesChange<AppNode>>(
    (nodeChanges) => {
      const [disallowedChanges, allowedChanged] = partition(
        nodeChanges,
        (change) => change.type === "remove",
      );
      if (disallowedChanges.length) {
        if (find(disallowedChanges, (change) => change.id === DRAFT_NODE_ID)) {
          toast.showToast({
            message: t("entity:create.toasts.no_delete_draft"),
            variant: "warning",
          });
        } else {
          toast.showToast({
            message: t("entity:create.toasts.no_delete_existing"),
            variant: "warning",
          });
        }
      }
      if (allowedChanged.length) {
        _onNodesChange(allowedChanged);
      }
    },
    [_onNodesChange, t, toast],
  );

  const onEdgesChange = useCallback<OnEdgesChange<AppEdge>>(
    (edgesChanges) => {
      const [allowedChanges, disallowedChanges] = partition(
        edgesChanges,
        (change) => {
          const edgeToDelete = find(
            getEdges(),
            (edges) => edges.id === (change as EdgeRemoveChange).id,
          );
          // only allow draft edges to be deleted
          // this is because if you select the draft node and press delete, we can block the node
          // delete but the edge deletes will still fire
          return (
            edgeToDelete?.id.includes(DRAFT_NODE_ID) || change.type !== "remove"
          );
        },
      );

      if (disallowedChanges.length) {
        toast.showToast({
          message: t("entity:create.toasts.no_existing_fund_structure_edit"),
          variant: "warning",
        });
      }

      if (allowedChanges.length) {
        _onEdgesChange(allowedChanges);
      }
    },
    [_onEdgesChange, getEdges, t, toast],
  );

  // this fires a lot so only show the message once
  const debouncedAlert = useMemo(
    () =>
      debounce(() => {
        toast.showToast({
          message: t("entity:create.toasts.no_commitment_edit"),
          variant: "warning",
        });
      }, 500),
    [t, toast],
  );

  const isValidConnection: IsValidConnection = useCallback(
    (connection) => {
      if (connection.target.includes(DRAFT_NODE_ID)) {
        debouncedAlert();
        return false;
      }
      return true;
    },
    [debouncedAlert],
  );
  return useMemo(
    () => ({ onNodesChange, onEdgesChange, isValidConnection }),
    [onNodesChange, onEdgesChange, isValidConnection],
  );
};
